import type {ServiceEnvironment} from '../utils/getTargetEnvironment';
import {
  getTargetEnvironment,
  getTunnelHost,
  getTurboHost,
  getDevboxHost,
} from '../utils/getTargetEnvironment';

export type ServiceName =
  | 'api'
  | 'graphql'
  | 'files'
  | 'site'
  | 'connect'
  | 'admin'
  | 'dashboard'
  | 'stripejs';

export const serviceEndpoints: Record<
  ServiceName,
  Record<ServiceEnvironment, string>
> = {
  api: {
    tunnel: getTunnelHost('api'),
    devbox: getDevboxHost('api'),
    turbo: getTurboHost('api'),
    qa: 'qa-api.stripe.com',
    preprod: 'preprod-api.stripe.com',
    prod: 'api.stripe.com',
  },
  graphql: {
    tunnel: getTunnelHost('graphql'),
    devbox: getDevboxHost('graphql'),
    turbo: getTurboHost('graphql'),
    qa: 'qa-graphql.stripe.com',
    preprod: 'preprod-graphql.stripe.com',
    prod: 'graphql.stripe.com',
  },
  files: {
    tunnel: getTunnelHost('upload'),
    devbox: getDevboxHost('upload'),
    turbo: getTurboHost('files'),
    qa: 'qa-files.stripe.com',
    preprod: 'preprod-files.stripe.com',
    prod: 'files.stripe.com',
  },
  site: {
    tunnel: getTunnelHost('site'),
    devbox: getDevboxHost('site'),
    turbo: getTurboHost('site'),
    qa: 'qa.stripe.com',
    preprod: 'preprod.stripe.com',
    prod: 'stripe.com',
  },
  connect: {
    tunnel: getTunnelHost('manage'),
    devbox: getDevboxHost('manage'),
    turbo: getTurboHost('connect'),
    qa: 'qa-connect.stripe.com',
    preprod: 'preprod-connect.stripe.com',
    prod: 'connect.stripe.com',
  },
  admin: {
    tunnel: getTunnelHost('admin'),
    devbox: getDevboxHost('admin'),
    turbo: getDevboxHost('admin'),
    qa: 'qa-dashboard.stripe.com',
    // Admin doesn't have preprod
    preprod: 'admin.qa.corp.stripe.com',
    prod: 'admin.corp.stripe.com',
  },
  dashboard: {
    tunnel: getTunnelHost('manage'),
    devbox: getDevboxHost('manage'),
    // PRR urls use the edge service name (as in dashboard.stripe.com) and not the development service name
    turbo: getTurboHost('dashboard'),
    qa: 'qa-dashboard.stripe.com',
    preprod: 'preprod-dashboard.stripe.com',
    prod: 'dashboard.stripe.com',
  },
  stripejs: {
    tunnel: getTunnelHost('stripejs'),
    devbox: getDevboxHost('stripejs'),
    turbo: getDevboxHost('stripejs'),
    qa: 'qa-js.stripe.com/v3/',
    preprod: 'js.stripe.com/v3/',
    prod: 'js.stripe.com/v3/',
  },
};

const buildStripeHostOverride = (
  service: ServiceName,
  targetEnvironment: ServiceEnvironment,
): string => {
  const environmentToUse = getTargetEnvironment(targetEnvironment);

  return serviceEndpoints[service][environmentToUse];
};

export const getStripeApiHost = (
  targetEnvironment: ServiceEnvironment,
): string => {
  return buildStripeHostOverride('api', targetEnvironment);
};

export const getStripeFilesHost = (
  targetEnvironment: ServiceEnvironment,
): string => {
  return buildStripeHostOverride('files', targetEnvironment);
};

export const getStripeSiteHost = (
  targetEnvironment: ServiceEnvironment,
): string => {
  return buildStripeHostOverride('site', targetEnvironment);
};

export const getStripeConnectHost = (
  targetEnvironment: ServiceEnvironment,
): string => {
  return buildStripeHostOverride('connect', targetEnvironment);
};

export const getStripeAdminHost = (
  targetEnvironment: ServiceEnvironment,
): string => {
  return buildStripeHostOverride('admin', targetEnvironment);
};

export const getStripeDashboardHost = (
  targetEnvironment: ServiceEnvironment,
): string => {
  return buildStripeHostOverride('dashboard', targetEnvironment);
};

export const getStripeJsHost = (
  targetEnvironment: ServiceEnvironment,
): string => {
  return buildStripeHostOverride('stripejs', targetEnvironment);
};

// This is to match the behavior of the existing recipient payouts service endpoints behavior for
// stripeJS. See (frontend/recipient-payouts/recipient-payouts-hosted-onboarding/src/data/api.ts)
// The two differences:
//   - The consumers of the stripeJSConfig under V2RecipientOnboarding expect
//     the endpoint to include the protocol (https://) but the submerchant-surfaces
//     stripe client utils do not include it so we are prepending it here.
//   - Recipient onboarding uses QA all environment except prod.
// TODO(blackjar): We will need to migrate recipient onboarding to be consistent with
// embedded https://jira.corp.stripe.com/browse/CARM-3246
export const getStripeJsHostForGPRecipientOnboarding = (
  targetEnvironment: ServiceEnvironment,
): string => {
  const env = targetEnvironment === 'prod' ? targetEnvironment : 'qa';
  return `https://${serviceEndpoints.stripejs[env]}`;
};

export const getStripeGraphqlHost = (
  targetEnvironment: ServiceEnvironment,
): string => {
  return buildStripeHostOverride('graphql', targetEnvironment);
};
